import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import DescriptionIcon from "@mui/icons-material/Description";
import { getToken } from "firebase/app-check";
import { getAppCheckInstance } from "../../config/ConfigFirebase";
import { pqrsdFunctionUrl } from "../../services/backend/PqrsdService";

// Citizen-side form for a requerimiento de información (CPACA art. 17).
//
// It is shown on the citizen's own status page — the email is the nudge, not
// the key — and talks to the pqrsdInfoReply function (multipart, because items
// can ask for documents).
//
// Everything here is written for someone who is not a lawyer and may be on a
// phone: plain language, one obvious action, visible progress, and answers that
// save themselves so going to look for a document never costs typing.
//
// mode="appeal" reuses it for the recurso de reposición against a
// desistimiento: the same pending points, plus the citizen's reasons, filed
// within the ten business days after the acto was notified.

const AUTOSAVE_MS = 3000;
const MIN_GROUNDS_LEN = 20;

function formatDate(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

function daysLeft(dueAt) {
  if (!dueAt) return null;
  const d = dueAt instanceof Date ? dueAt : new Date(dueAt);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

function deadlineWording(left) {
  if (left == null) return "Con plazo abierto";
  if (left < 0) return "El plazo venció";
  if (left === 0) return "Vence hoy";
  if (left === 1) return "Queda 1 día";
  return `Quedan ${left} días`;
}

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CitizenInfoRequestForm({ token, radicado, onReplied, mode = "info" }) {
  const isAppeal = mode === "appeal";
  const url = useMemo(
    () => process.env.REACT_APP_PQRSD_INFO_REPLY_URL || pqrsdFunctionUrl("pqrsdInfoReply"),
    []
  );

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [grounds, setGrounds] = useState("");
  const [files, setFiles] = useState({});
  const [missing, setMissing] = useState([]);
  const [done, setDone] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  const autosaveRef = useRef(null);

  const call = useCallback(async ({ action, includeFiles = false }) => {
    const appCheck = getAppCheckInstance();
    if (!appCheck) throw new Error("No pudimos validar tu navegador. Recarga la página e inténtalo de nuevo.");
    // No forced refresh: the SDK caches the token and renews it before it
    // expires. Forcing one per call exchanged a fresh token on every 3 s
    // autosave, and App Check throttles that (HTTP 429) within the session.
    const { token: appCheckToken } = await getToken(appCheck);

    const form = new FormData();
    form.append("payload", JSON.stringify({ token, action, answers, ...(isAppeal ? { grounds } : {}) }));
    if (includeFiles) {
      // The field name is the item id, which is how the function pairs an
      // upload with the document it answers.
      Object.entries(files).forEach(([itemId, file]) => {
        if (file) form.append(itemId, file, file.name);
      });
    }
    const resp = await fetch(url, {
      method: "POST",
      headers: { "X-Firebase-AppCheck": appCheckToken },
      body: form,
    });
    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json?.result) {
      const err = new Error(json?.error || "No pudimos completar la operación. Inténtalo de nuevo.");
      err.missing = json?.missing || [];
      err.request = json?.request || null;
      throw err;
    }
    return json;
  }, [url, token, answers, files, grounds, isAppeal]);

  // Initial load of the items.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const json = await call({ action: "load" });
        if (!active) return;
        setRequest(json.request);
        const seeded = {};
        (json.request?.items || []).forEach((it) => {
          if (it.kind === "question" && it.answer) seeded[it.id] = it.answer;
        });
        setAnswers(seeded);
        if (json.request?.appeal?.grounds) setGrounds(json.request.appeal.grounds);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // Deliberately once: `call` changes with every keystroke (answers are in
    // its closure), and reloading the form would wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced draft autosave (answers only — files upload on send).
  useEffect(() => {
    if (!request || done || !dirtyRef.current) return undefined;
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => {
      setSaving(true);
      call({ action: "save_draft" })
        .then(() => { dirtyRef.current = false; setSavedAt(new Date()); })
        .catch(() => { /* silent: they can still submit */ })
        .finally(() => setSaving(false));
    }, AUTOSAVE_MS);
    return () => { if (autosaveRef.current) clearTimeout(autosaveRef.current); };
  }, [answers, grounds, request, done, call]);

  const setAnswer = (id, value) => {
    dirtyRef.current = true;
    setMissing((prev) => prev.filter((m) => m !== id));
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const updateGrounds = (value) => {
    dirtyRef.current = true;
    setMissing((prev) => prev.filter((m) => m !== "grounds"));
    setGrounds(value);
  };

  const setFile = (id, file) => {
    setMissing((prev) => prev.filter((m) => m !== id));
    setFiles((prev) => ({ ...prev, [id]: file }));
  };

  const items = useMemo(
    () => [...(request?.items || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [request]
  );

  const isSatisfied = useCallback(
    (item) =>
      item.kind === "file"
        ? !!(files[item.id] || item.file)
        : !!String(answers[item.id] || "").trim(),
    [answers, files]
  );

  const pending = items.filter((it) => it.required && !isSatisfied(it));
  const completed = items.filter(isSatisfied).length;
  const groundsMissing = isAppeal && grounds.trim().length < MIN_GROUNDS_LEN;

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMissing([]);
    try {
      const json = await call({ action: isAppeal ? "submit_appeal" : "submit", includeFiles: true });
      setDone(json);
      if (onReplied) onReplied(json);
    } catch (e) {
      setError(e.message);
      setMissing(e.missing || []);
      if (e.request) setRequest(e.request);
    } finally {
      setBusy(false);
    }
  };

  const requestExtension = async () => {
    setBusy(true);
    setError(null);
    try {
      const json = await call({ action: "request_extension" });
      setRequest((prev) => (prev ? { ...prev, dueAt: json.dueAt, extensionGranted: true } : prev));
      setNotice(`Listo, ampliamos tu plazo hasta el ${formatDate(json.dueAt)}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, mb: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error && !request) {
    return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
  }

  if (!request) return null;

  // --- Sent: confirmation, and nothing else to do ---------------------------
  if (done && isAppeal) {
    return (
      <Alert
        severity="success"
        icon={<CheckCircleIcon fontSize="inherit" />}
        sx={{ mb: 3, alignItems: "flex-start" }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>Recibimos tu recurso de reposición</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          La entidad lo estudiará junto con la información que aportaste y lo resolverá mediante una decisión
          motivada{done.decisionDueAt ? `, a más tardar el ${formatDate(done.decisionDueAt)}` : ""}.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Te enviamos una constancia por correo y te avisaremos cuando se resuelva.
        </Typography>
      </Alert>
    );
  }

  if (done) {
    return (
      <Alert
        severity="success"
        icon={<CheckCircleIcon fontSize="inherit" />}
        sx={{ mb: 3, alignItems: "flex-start" }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>¡Gracias! Recibimos tu información</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Ya retomamos el trámite de tu solicitud{radicado ? ` ${radicado}` : ""}.
          {done.deadline
            ? ` Te responderemos a más tardar el ${formatDate(done.deadline)}.`
            : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Te enviamos una confirmación por correo. No necesitas hacer nada más.
        </Typography>
      </Alert>
    );
  }

  const left = daysLeft(request.dueAt);
  const urgent = left != null && left <= 3;

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderColor: urgent ? "error.main" : "warning.main", borderWidth: 2 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {isAppeal ? "Puedes interponer un recurso de reposición" : "Necesitamos un poco más de información"}
        </Typography>
        <Chip
          size="small"
          color={urgent ? "error" : "warning"}
          icon={<ScheduleIcon />}
          // The recurso runs in business days; a calendar-day countdown would
          // promise days the citizen does not have. Show the last day itself.
          label={isAppeal
            ? (left != null && left < 0 ? "El plazo venció" : `Hasta el ${formatDate(request.appeal?.lastDay || request.dueAt)}`)
            : deadlineWording(left)}
        />
      </Stack>

      {isAppeal ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La entidad archivó tu solicitud porque no recibió a tiempo la información que te pidió. Si no estás
          de acuerdo, puedes pedir que revise esa decisión hasta el{" "}
          <strong>{formatDate(request.appeal?.lastDay || request.dueAt)}</strong>. Para hacerlo, cuéntanos tus
          motivos y <strong>aporta la información que faltaba</strong>.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Para poder responderte de fondo necesitamos que completes los datos de abajo. Tienes plazo
          hasta el <strong>{formatDate(request.dueAt)}</strong>. Mientras tanto{" "}
          <strong>el tiempo de respuesta de la entidad está detenido</strong>: vuelve a correr al día
          siguiente de que nos envíes lo que falta.
        </Typography>
      )}

      {isAppeal && (request.appeal?.actoFileUrl || request.appeal?.actoFiledId) && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DescriptionIcon />}
          href={request.appeal.actoFileUrl || undefined}
          target="_blank"
          rel="noopener"
          disabled={!request.appeal.actoFileUrl}
          sx={{ mb: 2, mr: 1 }}
        >
          Ver el acto de desistimiento {request.appeal.actoFiledId ? `(${request.appeal.actoFiledId})` : ""}
        </Button>
      )}

      {(request.fileUrl || request.referenceId) && (
        // The official document behind this form. The citizen is its addressee:
        // they get to read what was required of them, signed and radicado.
        <Button
          size="small"
          variant="outlined"
          startIcon={<DescriptionIcon />}
          href={request.fileUrl || `/filed/view/${encodeURIComponent(request.referenceId || "")}`}
          target="_blank"
          rel="noopener"
          sx={{ mb: 2 }}
        >
          Ver el requerimiento {request.filedId ? `(${request.filedId})` : "oficial"}
        </Button>
      )}

      {request.note && (
        <Alert severity="info" sx={{ mb: 2 }}>{request.note}</Alert>
      )}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {completed} de {items.length} {items.length === 1 ? "punto resuelto" : "puntos resueltos"}
          </Typography>
          {saving ? (
            <Typography variant="caption" color="text.secondary">Guardando…</Typography>
          ) : savedAt ? (
            <Typography variant="caption" color="text.secondary">Borrador guardado</Typography>
          ) : null}
        </Stack>
        <LinearProgress
          variant="determinate"
          value={items.length ? (completed / items.length) * 100 : 0}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {isAppeal && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            ¿Por qué no estás de acuerdo con el archivo?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Explica tus motivos con tus palabras. Abajo aporta la información que te habían pedido.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            sx={{ mt: 1 }}
            value={grounds}
            error={missing.includes("grounds")}
            onChange={(e) => updateGrounds(e.target.value)}
            placeholder="Por ejemplo: no recibí el correo a tiempo, y aquí envío lo que faltaba."
            helperText={groundsMissing ? `Mínimo ${MIN_GROUNDS_LEN} caracteres.` : " "}
          />
        </Box>
      )}

      <Stack spacing={2} divider={<Divider flexItem />}>
        {items.map((item, idx) => {
          const satisfied = isSatisfied(item);
          const flagged = missing.includes(item.id);
          const chosen = files[item.id];
          return (
            <Box key={item.id}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {satisfied ? (
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mt: 0.3 }} />
                ) : (
                  <RadioButtonUncheckedIcon
                    fontSize="small"
                    color={flagged ? "error" : "disabled"}
                    sx={{ mt: 0.3 }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {idx + 1}. {item.kind === "file" ? item.label : item.header}
                    {!item.required && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {" "}(opcional)
                      </Typography>
                    )}
                  </Typography>
                  {item.help && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {item.help}
                    </Typography>
                  )}

                  {item.kind === "file" ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                      <Button
                        component="label"
                        size="small"
                        variant={satisfied ? "outlined" : "contained"}
                        startIcon={<UploadFileIcon />}
                        color={flagged ? "error" : "primary"}
                      >
                        {chosen || item.file ? "Cambiar archivo" : "Adjuntar archivo"}
                        <input
                          hidden
                          type="file"
                          accept={item.accept?.length ? item.accept.join(",") : undefined}
                          onChange={(e) => setFile(item.id, e.target.files?.[0] || null)}
                        />
                      </Button>
                      {chosen ? (
                        <>
                          <Typography variant="body2">
                            {chosen.name} <Typography component="span" variant="caption" color="text.secondary">
                              ({humanSize(chosen.size)})
                            </Typography>
                          </Typography>
                          <Tooltip title="Quitar">
                            <IconButton size="small" onClick={() => setFile(item.id, null)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : item.file ? (
                        <Typography variant="body2" color="success.main">
                          Ya enviaste {item.file.name}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          PDF, imagen o documento de hasta 10 MB
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      sx={{ mt: 1 }}
                      value={answers[item.id] || ""}
                      error={flagged}
                      onChange={(e) => setAnswer(item.id, e.target.value)}
                      placeholder="Escribe tu respuesta aquí"
                    />
                  )}
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ mt: 3 }}>
        {(pending.length > 0 || groundsMissing) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Te falta: {[
              ...(groundsMissing ? ["tus motivos"] : []),
              ...pending.map((it) => (it.kind === "file" ? it.label : it.header)),
            ].join(", ")}.
          </Typography>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SendIcon />}
            onClick={submit}
            disabled={busy || pending.length > 0 || groundsMissing}
            fullWidth={false}
          >
            {busy ? "Enviando…" : isAppeal ? "Interponer recurso" : "Enviar información"}
          </Button>
          {!isAppeal && !request.extensionGranted && left != null && left >= 0 && (
            <Button variant="text" onClick={requestExtension} disabled={busy}>
              Necesito más tiempo
            </Button>
          )}
        </Stack>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        {isAppeal
          ? "Lo que escribes se guarda solo. Si no interpones el recurso dentro del plazo, el archivo quedará en firme (artículos 74 a 77 de la Ley 1437 de 2011); siempre podrás radicar una nueva solicitud con la información completa."
          : "Lo que escribes se guarda solo, así que puedes salir a buscar un documento y volver. Si no recibimos la información dentro del plazo, la entidad puede archivar la solicitud por desistimiento tácito (artículo 17 de la Ley 1755 de 2015); podrás radicar una nueva cuando quieras."}
      </Typography>
    </Paper>
  );
}
